import { Activity, Rocket, Smartphone } from "lucide-react";
import { ArrowLink } from "@/components/blocks/ArrowLink";
import { NightDecor } from "@/components/blocks/decor/Decor";
import { ExternalLinkButton } from "@/components/blocks/ExternalLinkButton";
import { ProductShot } from "@/components/blocks/ProductShot";
import { QuestScene } from "@/components/blocks/scenes/QuestScene";
import { CardGrid, toCardItems } from "@/components/blocks/sections/CardGrid";
import { Checklist } from "@/components/blocks/sections/Checklist";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import {
	Overline,
	SectionHeading,
} from "@/components/blocks/sections/SectionHeading";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/motion";
import { QUESTOVERY_URL } from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";

const WHY_ICONS = {
	operations: Activity,
	longevity: Rocket,
	stores: Smartphone,
} as const;

export function ProductsPage() {
	const { dict } = useI18n();
	const page = dict.products;
	const questovery = page.questovery;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
			/>

			<Section>
				<Reveal
					as="section"
					className="dark relative isolate grid gap-12 overflow-hidden rounded-[2rem] border bg-background p-8 text-foreground md:p-14 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center"
				>
					<div aria-hidden="true" className="absolute inset-0 -z-10">
						<NightDecor />
					</div>
					<div className="space-y-7">
						<Overline>{questovery.badge}</Overline>
						<h2 className="text-[clamp(2.4rem,5.5vw,4.6rem)] leading-[0.95] font-semibold tracking-[-0.07em]">
							{questovery.title}
						</h2>
						<p className="max-w-xl text-xl text-pretty">{questovery.tagline}</p>
						<p className="max-w-xl text-pretty text-muted-foreground">
							{questovery.description}
						</p>
						<Checklist
							className="grid gap-3 sm:grid-cols-2 sm:space-y-0"
							items={Object.values(questovery.features)}
						/>
						<div className="flex flex-col gap-4 pt-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-8">
							<ExternalLinkButton
								href={QUESTOVERY_URL}
								cta="questovery"
								placement="products_card"
							>
								{questovery.link}
							</ExternalLinkButton>
							<ArrowLink to="/{-$locale}/realisations/questovery">
								{questovery.caseLink}
							</ArrowLink>
						</div>
					</div>
					<ProductShot
						format="phone"
						alt={questovery.visualAlt}
						fallback={<QuestScene />}
					/>
				</Reveal>
			</Section>

			<Section tone="stone">
				<SectionHeading
					index="01"
					label={page.why.label}
					title={page.why.title}
				/>
				<CardGrid
					className="mt-14"
					items={toCardItems(page.why.items, { icons: WHY_ICONS })}
				/>
			</Section>

			<Section>
				<RevealGroup className="grid gap-5 md:grid-cols-2">
					<RevealItem className="flex flex-col gap-4 rounded-3xl border bg-card p-8 md:p-10">
						<h2 className="text-2xl font-semibold tracking-[-0.025em] text-balance">
							{page.whiteLabel.title}
						</h2>
						<p className="text-pretty text-muted-foreground">
							{page.whiteLabel.description}
						</p>
						<div className="mt-auto pt-4">
							<ExternalLinkButton
								href={QUESTOVERY_URL}
								cta="questovery"
								placement="products_white_label"
								variant="outline"
							>
								{page.whiteLabel.link}
							</ExternalLinkButton>
						</div>
					</RevealItem>
					<RevealItem className="dot-grid flex flex-col gap-4 rounded-3xl border border-dashed p-8 md:p-10">
						<h2 className="text-2xl font-semibold tracking-[-0.025em] text-balance">
							{page.upcoming.title}
						</h2>
						<p className="text-pretty text-muted-foreground">
							{page.upcoming.description}
						</p>
					</RevealItem>
				</RevealGroup>
			</Section>
		</>
	);
}
